import React, { useState, useEffect } from 'react';
import { investmentAPI } from '../../services/api';
import { Edit, Save, X, Settings, Trash2, FileDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import * as XLSX from 'xlsx';

const PortfolioTab = ({ investment, onUpdate }) => {
  const { user } = useAuth();
  const { canEdit = false, canDownload = false } = usePermissions() || {};
  const [isEditing, setIsEditing] = useState(false);
  const [showCustomFieldsManager, setShowCustomFieldsManager] = useState(false);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [fundStages, setFundStages] = useState([]);
  const [formData, setFormData] = useState({
    portfolio_company_name: investment.portfolio_company_name,
    sector: investment.sector || '',
    types_of_securities_offered: investment.types_of_securities_offered || [],
    investment_stage: investment.investment_stage || 'Pipeline',
    custom_fields: investment.custom_fields || {}
  });
  const [newSecurity, setNewSecurity] = useState('');
  const [loading, setLoading] = useState(false);
  const [newCustomField, setNewCustomField] = useState({
    name: '',
    type: 'text',
    options: []
  });

  useEffect(() => {
    loadCustomFieldDefinitions();
    loadFundStages();
  }, [investment.id]);

  const loadFundStages = async () => {
    try {
      const response = await investmentAPI.getFundStages(investment.fund_id);
      setFundStages(response.data.stages || ['Pipeline', 'Active', 'Under Review', 'Hold', 'Watchlist', 'Exited']);
    } catch (error) {
      console.error('Failed to load fund stages:', error);
      setFundStages(['Pipeline', 'Active', 'Under Review', 'Hold', 'Watchlist', 'Exited']);
    }
  };

  const loadCustomFieldDefinitions = () => {
    const saved = localStorage.getItem(`portfolio_custom_fields_${investment.id}`);
    if (saved) {
      setCustomFieldDefinitions(JSON.parse(saved));
    }
  };

  const saveCustomFieldDefinitions = (definitions) => {
    localStorage.setItem(`portfolio_custom_fields_${investment.id}`, JSON.stringify(definitions));
    setCustomFieldDefinitions(definitions);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await investmentAPI.update(investment.id, formData);
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      alert('Failed to update investment');
    } finally {
      setLoading(false);
    }
  };

  const addSecurity = () => {
    if (newSecurity.trim()) {
      setFormData({
        ...formData,
        types_of_securities_offered: [...formData.types_of_securities_offered, newSecurity.trim()],
      });
      setNewSecurity('');
    }
  };

  const removeSecurity = (index) => {
    setFormData({
      ...formData,
      types_of_securities_offered: formData.types_of_securities_offered.filter((_, i) => i !== index),
    });
  };

  const handleCustomFieldChange = (fieldName, value) => {
    setFormData({
      ...formData,
      custom_fields: {
        ...formData.custom_fields,
        [fieldName]: value
      }
    });
  };

  const addCustomField = () => {
    if (!newCustomField.name.trim()) {
      alert('Field name is required');
      return;
    }

    const field = {
      id: Date.now().toString(),
      name: newCustomField.name.trim(),
      type: newCustomField.type,
      options: newCustomField.options
    };

    const updated = [...customFieldDefinitions, field];
    saveCustomFieldDefinitions(updated);
    setNewCustomField({ name: '', type: 'text', options: [] });
  };

  const removeCustomField = (fieldId) => {
    if (!window.confirm('Remove this custom field? Data will be lost.')) return;
    const updated = customFieldDefinitions.filter(f => f.id !== fieldId);
    saveCustomFieldDefinitions(updated);
  };

  const renderCustomField = (field, viewMode = false) => {
    const value = formData.custom_fields[field.name] || '';

    if (viewMode) {
      if (!value) return <p className="text-gray-500">Not specified</p>;
      if (field.type === 'checkbox') {
        return <p className="text-lg">{value === true || value === 'true' ? 'Yes' : 'No'}</p>;
      }
      if (field.type === 'textarea') {
        return <div className="text-lg prose max-w-none" dangerouslySetInnerHTML={{ __html: value }} />;
      }
      return <p className="text-lg">{value}</p>;
    }

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
        );
      case 'number':
        return (
          <input
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
        );
      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Select...</option>
            {field.options.map((opt, idx) => (
              <option key={idx} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {field.options.map((opt, idx) => (
              <label key={idx} className="flex items-center">
                <input
                  type="radio"
                  name={field.name}
                  value={opt}
                  checked={value === opt}
                  onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                  className="mr-2"
                />
                {opt}
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => handleCustomFieldChange(field.name, e.target.checked)}
              className="mr-2"
            />
            Yes
          </label>
        );
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            rows="4"
            placeholder="Multi-line text..."
          />
        );
      default:
        return null;
    }
  };

  if (!isEditing) {
    const handleExportToExcel = () => {
      // Prepare data as key-value pairs for portfolio information
      const exportData = [
        { Field: 'Company Name', Value: investment.portfolio_company_name || '' },
        { Field: 'Sector', Value: investment.sector || '' },
        { Field: 'Investment Currency', Value: investment.investment_currency || '' },
        { Field: 'Types of Securities', Value: (investment.types_of_securities_offered || []).join(', ') },
      ];

      // Add custom fields
      if (investment.custom_fields) {
        Object.entries(investment.custom_fields).forEach(([key, value]) => {
          exportData.push({ Field: key, Value: value });
        });
      }

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Portfolio Info');

      // Auto-size columns
      ws['!cols'] = [{ wch: 30 }, { wch: 50 }];

      // Generate file
      XLSX.writeFile(wb, `${investment.portfolio_company_name || 'Investment'}_Portfolio.xlsx`);
    };

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">Portfolio Information</h3>
          <div className="flex space-x-2">
            <button
              onClick={handleExportToExcel}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              title="Export to Excel"
            >
              <FileDown size={18} className="mr-2" />
              Export Excel
            </button>
            {canEdit && (
              <>
                <button
                  onClick={() => setShowCustomFieldsManager(!showCustomFieldsManager)}
                  className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  <Settings size={18} className="mr-2" />
                  Custom Fields
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  <Edit size={18} className="mr-2" />
                  Edit
                </button>
              </>
            )}
          </div>
        </div>

        {/* Custom Fields Manager */}
        {showCustomFieldsManager && (
          <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold">Manage Custom Fields</h4>
              <button onClick={() => setShowCustomFieldsManager(false)} className="text-gray-500">
                <X size={20} />
              </button>
            </div>

            <div className="mb-4 p-4 bg-white rounded border">
              <h5 className="font-medium mb-3">Add New Custom Field</h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  placeholder="Field Name"
                  value={newCustomField.name}
                  onChange={(e) => setNewCustomField({ ...newCustomField, name: e.target.value })}
                  className="px-3 py-2 border rounded"
                />
                <select
                  value={newCustomField.type}
                  onChange={(e) => setNewCustomField({ ...newCustomField, type: e.target.value, options: [] })}
                  className="px-3 py-2 border rounded"
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="dropdown">Dropdown</option>
                  <option value="radio">Radio Button</option>
                  <option value="checkbox">Checkbox</option>
                  <option value="textarea">Multi-line Text</option>
                </select>
                <button
                  onClick={addCustomField}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Add Field
                </button>
              </div>

              {(newCustomField.type === 'dropdown' || newCustomField.type === 'radio') && (
                <div className="mt-3">
                  <label className="block text-sm font-medium mb-2">Options (comma-separated):</label>
                  <input
                    type="text"
                    placeholder="Option1, Option2, Option3"
                    onChange={(e) => setNewCustomField({
                      ...newCustomField,
                      options: e.target.value.split(',').map(o => o.trim()).filter(o => o)
                    })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
              )}
            </div>

            <div>
              <h5 className="font-medium mb-2">Existing Custom Fields:</h5>
              {customFieldDefinitions.length === 0 ? (
                <p className="text-gray-500 text-sm">No custom fields defined yet</p>
              ) : (
                <div className="space-y-2">
                  {customFieldDefinitions.map((field) => (
                    <div key={field.id} className="flex items-center justify-between p-3 bg-white border rounded">
                      <div>
                        <span className="font-medium">{field.name}</span>
                        <span className="ml-2 text-sm text-gray-600">({field.type})</span>
                        {field.options.length > 0 && (
                          <span className="ml-2 text-sm text-gray-500">
                            [{field.options.join(', ')}]
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => removeCustomField(field.id)}
                        className="text-red-600 hover:bg-red-50 p-1 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600">Portfolio Company Name</p>
            <p className="text-lg font-semibold">{investment.portfolio_company_name}</p>
          </div>

          <div>
            <p className="text-sm text-gray-600">Sector</p>
            <p className="text-lg">{investment.sector || 'Not specified'}</p>
          </div>

          <div>
            <p className="text-sm text-gray-600">Investment Stage</p>
            <span className="inline-block px-3 py-1 mt-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-medium">
              {investment.investment_stage || 'Pipeline'}
            </span>
          </div>

          <div>
            <p className="text-sm text-gray-600">Types of Securities Offered</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {investment.types_of_securities_offered && investment.types_of_securities_offered.length > 0 ? (
                investment.types_of_securities_offered.map((type, idx) => (
                  <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    {type}
                  </span>
                ))
              ) : (
                <p className="text-gray-500">No securities specified</p>
              )}
            </div>
          </div>

          {/* Display Custom Fields */}
          {customFieldDefinitions.map((field) => (
            <div key={field.id}>
              <p className="text-sm text-gray-600">{field.name}</p>
              {renderCustomField(field, true)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold">Edit Portfolio Information</h3>
        <div className="flex space-x-2">
          <button
            onClick={() => setIsEditing(false)}
            className="flex items-center px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
          >
            <X size={18} className="mr-2" />
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
          >
            <Save size={18} className="mr-2" />
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Portfolio Company Name
          </label>
          <input
            type="text"
            value={formData.portfolio_company_name}
            onChange={(e) => setFormData({ ...formData, portfolio_company_name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Sector</label>
          <input
            type="text"
            value={formData.sector}
            onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Investment Stage</label>
          <select
            value={formData.investment_stage}
            onChange={(e) => setFormData({ ...formData, investment_stage: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          >
            {fundStages.map((stage) => (
              <option key={stage} value={stage}>{stage}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Types of Securities Offered
          </label>
          <div className="flex space-x-2 mb-2">
            <input
              type="text"
              value={newSecurity}
              onChange={(e) => setNewSecurity(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSecurity())}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
              placeholder="Add security type"
            />
            <button
              type="button"
              onClick={addSecurity}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.types_of_securities_offered.map((type, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
              >
                {type}
                <button
                  type="button"
                  onClick={() => removeSecurity(index)}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Custom Fields */}
        {customFieldDefinitions.map((field) => (
          <div key={field.id}>
            <label className="block text-sm font-medium text-gray-700 mb-2">{field.name}</label>
            {renderCustomField(field)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PortfolioTab;
